/**
 * TrueLine chart theme.
 *
 * Charts previously passed stock Tailwind hexes (#3b82f6, #22c55e, …) straight
 * to recharts, which bypassed the palette entirely and left them reading blue
 * and green on a sand canvas. Everything chart-coloured now comes from here.
 *
 * The categorical ramps were selected against the dataviz six-checks validator
 * rather than by eye, and both modes pass lightness band, chroma floor, CVD
 * separation and the normal-vision floor:
 *
 *   light  worst adjacent CVD dE 10.6 (deutan) · normal-vision floor 25.2
 *   dark   worst adjacent CVD dE 10.9 (deutan) · normal-vision floor 19.3
 *
 * Two constraints worth knowing before editing:
 *
 * 1. FIVE slots, in fixed order. Dark is a *selected* set of steps, not an
 *    automatic lightening of light — the dark surface forces a tighter
 *    lightness band (L 0.48-0.67 vs 0.43-0.77). A sixth warm hue could not be
 *    separated from either the teal or the olive at any step, so a 6th series
 *    folds into "Other" rather than getting a generated colour.
 * 2. The order is load-bearing. CVD separation is measured on *adjacent* pairs,
 *    and amber/olive collapse under deutan when placed next to each other —
 *    plum sits between them deliberately. Reordering these needs a re-run of
 *    scripts/validate_palette.js, not a judgement call.
 *
 * In light mode amber sits at 2.48:1 against the surface, which obligates the
 * visible labels and legend the dashboards already carry — don't ship an
 * amber series as a bare unlabelled mark.
 */

export type ChartMode = 'light' | 'dark';

// Fixed categorical order — assign by slot, never cycle.
const SERIES_LIGHT = [
  '#C24A22', // coral
  '#008C74', // teal
  '#DE9220', // amber
  '#96345F', // plum
  '#4E7C18', // olive
];

const SERIES_DARK = [
  '#D85A30', // coral
  '#00A183', // teal
  '#C4780F', // amber
  '#B04070', // plum
  '#639922', // olive
];

// Status is reserved — never reuse these as "series 6".
const STATUS_LIGHT = {
  good: '#3B6D11',
  warning: '#854F0B',
  serious: '#AD501A',
  critical: '#A32D2D',
};

const STATUS_DARK = {
  good: '#82B443',
  warning: '#F0AE47',
  serious: '#DC7E42',
  critical: '#DE6E6E',
};

export interface ChartTheme {
  /** Categorical series, in fixed slot order. */
  series: string[];
  /** Reserved status colours — pair with an icon or label, never colour alone. */
  status: typeof STATUS_LIGHT;
  /** Recessive gridlines. */
  grid: string;
  /** Axis lines, ticks and labels. */
  axis: string;
  /** Single-hue sequential ramp, light -> dark. */
  sequential: string[];
  tooltip: {
    background: string;
    border: string;
    text: string;
  };
}

const LIGHT: ChartTheme = {
  series: SERIES_LIGHT,
  status: STATUS_LIGHT,
  grid: '#E4E2D9',
  axis: '#888780',
  sequential: ['#FAECE7', '#F5C4B3', '#F0997B', '#D85A30', '#993C1D', '#4A1B0C'],
  tooltip: {
    background: '#FFFFFF',
    border: '#D3D1C7',
    text: '#2C2C2A',
  },
};

const DARK: ChartTheme = {
  series: SERIES_DARK,
  status: STATUS_DARK,
  grid: '#444441',
  axis: '#888780',
  sequential: ['#331107', '#4A1B0C', '#993C1D', '#D85A30', '#F0997B', '#FAECE7'],
  tooltip: {
    background: '#2C2C2A',
    border: '#444441',
    text: '#F1EFE8',
  },
};

export const getChartTheme = (mode: ChartMode): ChartTheme => (mode === 'dark' ? DARK : LIGHT);

/**
 * Colour for series slot `index`. Past the ramp it deliberately clamps to the
 * last slot rather than cycling — a cycled hue silently reuses an identity that
 * already belongs to another series.
 */
export const seriesColor = (mode: ChartMode, index: number): string => {
  const { series } = getChartTheme(mode);
  return series[Math.min(index, series.length - 1)];
};

/** Recharts <Tooltip/> props wired to the current surface. */
export const tooltipProps = (mode: ChartMode) => {
  const t = getChartTheme(mode);
  return {
    contentStyle: {
      background: t.tooltip.background,
      border: `1px solid ${t.tooltip.border}`,
      borderRadius: 12,
      color: t.tooltip.text,
      fontSize: 12.5,
      boxShadow: '0 4px 16px rgba(44, 44, 42, 0.08)',
    },
    labelStyle: { color: t.tooltip.text, fontWeight: 600 },
    itemStyle: { color: t.tooltip.text },
  };
};
